import React, { useEffect, useState } from "react";
import { MetricCard } from "../components/MetricCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "../hooks/useAuth";
import { API_BASE } from "@/lib/api";
import { LucideProps } from "lucide-react";
import {
  Home,
  Users,
  DollarSign,
  AlertTriangle,
  Plus,
  Loader2
} from "lucide-react";

interface Property {
  id: string;
  codigo: string;
  logradouro: string;
  bairro: string;
  cidade: string;
  uf: string;
  valorAluguel: number;
  ativo: boolean;
}

interface DashboardMetrics {
  totalProperties: number;
  activeProperties: number;
  totalRevenue: number;
  pendingIssues: number;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalProperties: 0,
    activeProperties: 0,
    totalRevenue: 0,
    pendingIssues: 0,
  });
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      
      // Fetch properties
      const propertiesRes = await fetch(`${API_BASE}/api/imoveis`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (propertiesRes.ok) {
        const data = await propertiesRes.json();
        const propertiesData = data.imoveis || [];
        setProperties(propertiesData);
        
        // Calculate metrics from properties
        const totalProperties = propertiesData.length;
        const activeProperties = propertiesData.filter((p: Property) => p.ativo).length;
        const totalRevenue = propertiesData
          .filter((p: Property) => p.ativo)
          .reduce((sum: number, p: Property) => sum + (p.valorAluguel || 0), 0);
        
        setMetrics({
          totalProperties,
          activeProperties,
          totalRevenue,
          pendingIssues: 0, // Placeholder
        });
      }
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return <div className="flex items-center justify-center h-screen">Carregando...</div>;

  if (loading) {
    return (
      <div className="min-h-screen bg-background font-sans flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100);
  };

  return (
    <div className="min-h-screen bg-background font-sans">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <Button onClick={() => window.location.href = "/properties/new"}>
            <Plus className="mr-2 h-4 w-4" />
            Novo Imóvel
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Total de Imóveis"
            value={metrics.totalProperties}
            icon={<Home className="h-5 w-5" />}
          />
          <MetricCard
            title="Imóveis Ativos"
            value={metrics.activeProperties}
            icon={<Home className="h-5 w-5" />}
          />
          <MetricCard
            title="Receita Mensal"
            value={formatCurrency(metrics.totalRevenue)}
            icon={<DollarSign className="h-5 w-5" />}
          />
          <MetricCard
            title="Pendências"
            value={metrics.pendingIssues}
            icon={<AlertTriangle className="h-5 w-5" />}
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Meus Imóveis</CardTitle>
          </CardHeader>
          <CardContent>
            {properties.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">Nenhum imóvel cadastrado ainda.</p>
                <Button onClick={() => window.location.href = "/properties/new"}>
                  <Plus className="mr-2 h-4 w-4" />
                  Cadastrar Primeiro Imóvel
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {properties.map((property) => (
                  <div key={property.id} className="flex items-center justify-between p-4 border rounded-lg bg-card hover:bg-accent/50 transition-colors">
                    <div className="flex-1">
                      <p className="font-medium">{property.codigo} - {property.logradouro}, {property.numero}</p>
                      <p className="text-sm text-muted-foreground">{property.bairro}, {property.cidade}/{property.uf}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={cn(
                        "px-2 py-1 rounded-full text-xs font-medium",
                        property.ativo 
                          ? "bg-green-100 text-green-800" 
                          : "bg-gray-100 text-gray-800"
                      )}>
                        {property.ativo ? "Ativo" : "Inativo"}
                      </span>
                      <span className="font-medium">{formatCurrency(property.valorAluguel)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Helper function for className
function cn(...inputs: any[]) {
  return inputs.filter(Boolean).join(" ");
}
